import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useUrlParams } from '../utils/url';
import { Organization, PractitionerDirectory } from 'src/shared/types';
import { apiGet } from 'src/utils/api';

interface DirectoryContextType {
  organization: Organization | null;
  specialties: string[];
  googleMapsKey: string;
  error: string | null;
  practitioner: PractitionerDirectory | null;
  setError: (error: string | null) => void;
  setPractitioner: (practitioner: PractitionerDirectory) => void;
  loading: boolean;
}

const DirectoryContext = createContext<DirectoryContextType | undefined>(undefined);

interface DirectoryProviderProps {
  children: ReactNode;
}

const DirectoryProvider = ({ children }: DirectoryProviderProps) => {
  const params = useUrlParams();
  const org_id = params.org_id;
  const practitioner_role_id = params.practitioner_role_id;
  const [ error, setError ] = useState<string | null>(null);
  const [ organization, setOrganization ] = useState<Organization | null>(null);
  const [ specialties, setSpecialties ] = useState<string[]>([]);
  const [ googleMapsKey, setGoogleMapsKey ] = useState<string>('');
  const [ practitioner, setPractitioner ] = useState<PractitionerDirectory | null>(null);
  const [ loading, setLoading ] = useState(false);

  const fetchOrganization = useCallback(async () => {
    setLoading(true);
    const response = await apiGet(`/api/organizations/${org_id}`);
    const data = await response.json();
    if (!data.error) {
      setOrganization(data.organization);
      setSpecialties(data.specialties);
      setGoogleMapsKey(data.google_maps_key || '');
    } else {
      setError("Organization not found");
      setOrganization(null);
      setSpecialties([]);
      setGoogleMapsKey('');
    }
    setLoading(false);
  }, [ org_id ]);

  const fetchPractitioner = useCallback(async () => {
    if (!organization) return;

    setLoading(true);
    const roleId =  practitioner?.practitioner_role_id || practitioner_role_id;
    const response = await apiGet(`/api/${org_id}/ehr/practitioners/${roleId}`);
    const data = await response.json();
    if (!data.error) {
      setPractitioner(data);
    } else {
      setError("Practitioner not found");
      setPractitioner(null);
    }
    setLoading(false);
  }, [ practitioner_role_id, practitioner, organization, org_id ]);

  useEffect(() => {
    if (org_id && !organization) {
      fetchOrganization();
    }
    if (practitioner_role_id && !practitioner && organization) {
      fetchPractitioner();
    }
  }, [ org_id, organization, practitioner_role_id, practitioner, fetchOrganization, fetchPractitioner ]);

  return (
    <DirectoryContext.Provider value={{
      organization,
      specialties,
      googleMapsKey,
      error,
      practitioner,
      setError,
      setPractitioner,
      loading,
    }}>
      {children}
    </DirectoryContext.Provider>
  );
};

export const useDirectory = (): DirectoryContextType => {
  const context = useContext(DirectoryContext);

  if (context === undefined) {
    throw new Error('useDirectory must be used within a DirectoryProvider');
  }

  return context;
};

export default DirectoryProvider;
